class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    // #CartDrawer innerHTML is replaced on cart updates — the overlay node is recreated,
    // so a direct listener on it would be lost. Delegate from the host element.
    this.addEventListener('click', this.onHostClick.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  onHostClick(event) {
    if (event.target.closest('#CartDrawer-Overlay')) {
      this.close();
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');

    if (cartLink !== null) {
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLink);
      });
      cartLink.addEventListener('keydown', (event) => {
        if (event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    }
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const emptyInner = this.querySelector('.drawer__inner-empty');
        const drawerInner = this.querySelector('.drawer__inner');
        const containerToTrapFocusOn =
          this.classList.contains('is-empty') && emptyInner
            ? emptyInner
            : drawerInner || document.getElementById('CartDrawer') || this;
        const focusElement = drawerInner || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState, options = {}) {
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (!sectionElement) return;

      if (section.id === 'cart-icon-bubble' && sectionElement.closest('.header-vet')) {
        this.renderCartIconBubbleForVetHeader(sectionElement, parsedState.sections[section.id]);
        return;
      }

      // A selector miss is not an error — sections/cart-icon-bubble.liquid renders a bare
      // fragment with no `.shopify-section` wrapper. Keep the existing DOM instead.
      const updatedHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      if (updatedHTML === null) return;
      sectionElement.innerHTML = updatedHTML;
    });

    // Liquid puts `is-empty` on <cart-drawer> when cart is empty (free gift off).
    // Only #CartDrawer innerHTML is replaced above — without this, `is-empty` stays
    // true, open() picks .drawer__inner-empty (missing for non-empty carts) and
    // trapFocus(null) throws (empty line area, wrong subtotal / bubble).
    const hasEmptyState = this.querySelector('.drawer__inner-empty');
    this.classList.toggle('is-empty', !!hasEmptyState);

    if (options.open !== false) {
      setTimeout(() => {
        this.open();
      });
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const section = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return section ? section.innerHTML : null;
  }

  renderCartIconBubbleForVetHeader(cartIconElement, sectionHtml) {
    if (!sectionHtml) return;
    const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const newSection = doc.querySelector('.shopify-section');
    if (!newSection) return;
    const newBubble = newSection.querySelector('.cart-count-bubble');
    const container = cartIconElement.querySelector('.header-vet__cart-icon-container');
    if (!container) return;
    const currentBubble = container.querySelector('.cart-count-bubble');
    if (newBubble) {
      const bubbleEl = newBubble.cloneNode(true);
      if (currentBubble) {
        currentBubble.replaceWith(bubbleEl);
      } else {
        container.appendChild(bubbleEl);
      }
    } else if (currentBubble) {
      currentBubble.remove();
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

if (typeof CartItems !== 'undefined') {
  class CartDrawerItems extends CartItems {
    getSectionsToRender() {
      return [
        {
          id: 'CartDrawer',
          section: 'cart-drawer',
          selector: '.drawer__inner',
        },
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section',
        },
      ];
    }
  }

  customElements.define('cart-drawer-items', CartDrawerItems);
}
